import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Modal, useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { Table } from '../../components/DataTable'
import { plans as plansApi, products as productsApi } from '../../api/fee'
import type { OnetimeProduct, Plan, Product, Subscription } from '../../api/types'
import { CatalogueEditor } from '../../components/CatalogueEditor'
import { PageTitle, QueryState } from '../../components/common'
import { useProject } from '../../layout/projectContext'
import { planLimits, subscriptionRowClass, subscriptionStatusLabel } from '../../lib/catalogue'
import { formatDateTime, formatPrice } from '../../lib/format'

type Kind = 'plan' | 'product'
type Item = Plan | Product

/**
 * Port of `AdminPlanListComponent` / `AdminProductListComponent`
 * (`projects/:projectId/admin/plans[/:planId]`, `…/admin/products[/:productId]`):
 * the catalogue with add/edit (B3b, admin only) and, per item, who is
 * subscribed (`GET …/admin/plans/{id}/subscriptions`) / who paid
 * (`GET …/admin/products/{id}/users`) — opened by the `:id` deep link.
 */
export function AdminCataloguePage({ kind }: { kind: Kind }) {
  const { projectId, project } = useProject()
  const params = useParams<{ planId?: string; productId?: string }>()
  const id = kind === 'plan' ? params.planId : params.productId
  const navigate = useNavigate()
  const list = useApiQuery<Item[]>(
    () => (kind === 'plan' ? plansApi.search(projectId) : productsApi.search(projectId)),
    [projectId, kind],
  )
  const [editing, setEditing] = useState<Item | 'new' | null>(null)
  const base = `/projects/${encodeURIComponent(projectId)}/admin/${kind === 'plan' ? 'plans' : 'products'}`
  const usersBase = `/projects/${encodeURIComponent(projectId)}/admin/users`

  const columns: Column<Item>[] = [
    {
      key: 'name',
      label: kind === 'plan' ? 'Plan name' : 'Product name',
      format: (_v, it) => (
        <>
          <Link to={`${base}/${encodeURIComponent(it.id)}`}>{it.name}</Link>
          {Boolean(it.hidden) && <span className="portal-muted-small">(hidden)</span>}
        </>
      ),
    },
    {
      key: 'price',
      label: 'Price',
      align: 'right',
      numeric: true,
      format: (_v, it) => (
        <>
          {formatPrice(it.price, it.currency)}
          {kind === 'product' && (
            <span className="portal-muted-small">{Number((it as Product).limit) === 0 ? 'payable anytime' : 'payable once'}</span>
          )}
        </>
      ),
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      format: (_v, it) => (
        <button type="button" onClick={() => setEditing(it)}>
          Edit
        </button>
      ),
    },
  ]
  const selected = id ? (list.data?.find((x) => x.id === id) ?? null) : null
  const title = kind === 'plan' ? 'Plans' : 'Products'

  return (
    <div className="portal-card">
      <PageTitle title={`${title} — ${project.name}`}>
        <button type="button" onClick={() => setEditing('new')}>
          {kind === 'plan' ? 'Add plan' : 'Add product'}
        </button>
      </PageTitle>
      <QueryState loading={list.loading} error={list.error}>
        <div className="portal-table-scroll">
          <Table columns={columns} rows={list.data ?? []} rowKey={(x) => x.id} />
        </div>
      </QueryState>
      {selected && (
        <Modal open title={selected.name} onClose={() => navigate(base)} className="portal-modal-wide">
          {kind === 'plan' ? (
            <PlanSubscribers projectId={projectId} plan={selected as Plan} usersBase={usersBase} />
          ) : (
            <ProductBuyers projectId={projectId} product={selected as Product} usersBase={usersBase} />
          )}
        </Modal>
      )}
      {editing && (
        <CatalogueEditor
          kind={kind}
          projectId={projectId}
          item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.refetch()
          }}
        />
      )}
    </div>
  )
}

function PlanSubscribers({ projectId, plan, usersBase }: { projectId: string; plan: Plan; usersBase: string }) {
  const q = useApiQuery(() => plansApi.subscriptionsOfPlan(projectId, plan.id), [projectId, plan.id])
  const limits = planLimits(plan).filter((l) => Number(l.value) >= 0)
  const columns: Column<Subscription & { idx: number }>[] = [
    { key: 'idx', label: '#', align: 'right', numeric: true },
    {
      key: 'email',
      label: 'Email',
      format: (v) => <Link to={`${usersBase}/paymentinfo?email=${encodeURIComponent(String(v))}`}>{String(v)}</Link>,
    },
    { key: 'status', label: 'Status', format: (_v, s) => subscriptionStatusLabel(s) },
    { key: 'expiresAt', label: 'Expires at', format: (v) => formatDateTime(v) },
    {
      key: 'manage',
      label: '',
      sortable: false,
      format: (_v, s) => (
        <Link className="portal-button-link" to={`${usersBase}/subscriptions?email=${encodeURIComponent(s.email)}`}>
          manage user&apos;s subscriptions
        </Link>
      ),
    },
  ]
  return (
    <div className="fs-modal-body">
      <div className="portal-grid-2">
        <div className="fs-panel">
          <h3>Monthly fee</h3>
          <p className="fs-amount">{formatPrice(plan.price, plan.currency)}</p>
        </div>
        {limits.length > 0 && (
          <div className="fs-panel">
            <h3>Limits</h3>
            <dl className="portal-kv">
              {limits.map((l) => (
                <div key={l.key} className="fs-kv-row">
                  <dt>{l.description || l.key}</dt>
                  <dd className="fs-num">{l.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </div>
      <h3>Subscribers</h3>
      <QueryState loading={q.loading} error={q.error}>
        <div className="portal-table-scroll">
          <Table
            columns={columns}
            rows={(q.data ?? []).map((s, i) => ({ ...s, idx: i + 1 }))}
            rowKey={(s) => s.email || s.idx}
            rowClassName={subscriptionRowClass}
          />
        </div>
      </QueryState>
    </div>
  )
}

function ProductBuyers({ projectId, product, usersBase }: { projectId: string; product: Product; usersBase: string }) {
  const q = useApiQuery(() => productsApi.usersOfProduct(projectId, product.id), [projectId, product.id])
  const columns: Column<OnetimeProduct & { idx: number }>[] = [
    { key: 'idx', label: '#', align: 'right', numeric: true },
    {
      key: 'email',
      label: 'Email',
      format: (v) => <Link to={`${usersBase}/paymentinfo?email=${encodeURIComponent(String(v))}`}>{String(v)}</Link>,
    },
    { key: 'payedAt', label: 'Last paid at', format: (v) => formatDateTime(v) },
    {
      key: 'manage',
      label: '',
      sortable: false,
      format: (_v, r) => (
        <Link className="portal-button-link" to={`${usersBase}/products?email=${encodeURIComponent(r.email)}`}>
          manage user&apos;s products
        </Link>
      ),
    },
  ]
  return (
    <div className="fs-modal-body">
      <div className="fs-panel">
        <h3>Price</h3>
        <p className="fs-amount">{formatPrice(product.price, product.currency)}</p>
      </div>
      <h3>Users who paid at least once</h3>
      <QueryState loading={q.loading} error={q.error}>
        <div className="portal-table-scroll">
          <Table columns={columns} rows={(q.data ?? []).map((r, i) => ({ ...r, idx: i + 1 }))} rowKey={(r) => r.email || r.idx} />
        </div>
      </QueryState>
    </div>
  )
}
